import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link, useLocation } from "react-router-dom";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import Chip from "../../../components/Chip";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import FilterReplaceOrders from "../../../api/sell-orders/filterReplaceOrders";
import { DownloadIcon, EyeOpen, PrintIcon } from "../../../assets/icons/Icons";
import SearchFilter from "../../../components/filters/SearchFilter";
import ViewReplaceOrder from "../../../components/sell-order/ViewReplaceOrder";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import { formatDate } from "../../../utils/formatDate";
import { useUserContext } from "../../../contexts/UserContext";
import ResetFilters from "../../../components/filters/ResetFilters";
import ExportDataButton from "../../../components/ExportDataButton";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../../components/CustomTooltip";
import SelectFilter from "../../../components/filters/SelectFilter";
import { getReplaceCardReceipt } from "../../../api/sell-orders/getReplaceCardReceipt";
import usePrint from "../../../hooks/usePrint";
import { CentsToDollars } from "../../../utils/formatPrice";
import { ExportReplaceOrders } from "../../../api/sell-orders/exportReplaceOrders";
import { downloadFile } from "../../../api/downloadFile";
import OfficesFilter from "../../../components/filters/OfficesFilter";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import ReplaceCardModal from "../../../components/sell-order/ReplaceCardModal";
import { MOD_SELL_ORDER } from "../../../contstants";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    setSelectedItem,
    showViewItem,
    setShowViewItem,
    showCreateItem,
    setShowCreateItem,
    refetch,
  } = useData(FilterReplaceOrders);

  const { can: userCan, hasPermission, hasDynamicPermissions } =
    useUserContext();

  const canCreate = hasDynamicPermissions()
    ? hasPermission("sell_order.replace_card", "write")
    : userCan(MOD_SELL_ORDER, "write");

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Replace Cards")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const { handlePrint: handlePrintReceipt } = usePrint(getReplaceCardReceipt);

  // Export CSV
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  const handleExportCSV = async () => {
    try {
      const response = await FilterReplaceOrders(-1, queryParams);
      const ids = response.data.map((item) => item.id);
      await downloadFile(
        ExportReplaceOrders,
        { ids: ids },
        "replacement_orders.csv"
      );
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <Layout title={t("Replace Cards")} breadcrumbs={t("Card Issuing")}>
      {showCreateItem && (
        <ReplaceCardModal
          onClose={() => setShowCreateItem(false)}
        />
      )}

      {showViewItem && (
        <ViewReplaceOrder
          order={selectedItem}
          onClose={() => setShowViewItem(false)}
        />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "user",
                    label: t("By User"),
                  },
                ]}
                defaultValue="user"
                placeholder={<div>{t("Search by")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider name="office_id">
              <OfficesFilter />
            </FilterContextProvider>
            <FilterContextProvider name="status">
              <SelectFilter
                options={[
                  {
                    value: "all",
                    label: t("Select Status"),
                  },
                  {
                    value: "success",
                    label: t("Success"),
                  },
                  {
                    value: "failed",
                    label: t("Failed"),
                  },
                ]}
                defaultValue="all"
                placeholder={<div>{t("Filter by Status")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            {canCreate && (
              <button
                className="btn btn-primary btn-medium"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Replace")}
              </button>
            )}
            <ExportDataButton
              className="btn btn-secondary btn-medium"
              onClick={handleExportCSV}
              label={t("Export Report")}
              disabled={!data.data?.length}
            />
          </>
        }
        columns={[
          { header: t("Order Number"), accessor: "id" },
          { header: t("Date"), accessor: "created_at" },
          { header: t("Order Type"), accessor: "type" },
          { header: t("Order Status"), accessor: "status" },
          { header: t("User"), accessor: "created_by_name" },
          { header: t("User ID"), accessor: "created_by" },
          {
            header: t("Total Replacement Amount"),
            accessor: "amount_transfered",
          },
          { header: t("Office"), accessor: "office_name" },
          { header: t("Actions"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) => (
          <div className="d-flex align-items-center">
            {item.status === "success" && (
              <>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrintReceipt({
                      requestParams: { id: item.id },
                      name: `replacement-order-${item.id}`,
                      width: "",
                      height: "",
                      download: false,
                    });
                  }}
                >
                  <CustomTooltip
                    icon={<PrintIcon />}
                    message={t("Print Receipt")}
                  />
                </Link>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrintReceipt({
                      requestParams: { id: item.id },
                      name: `replacement-order-${item.id}`,
                      width: "",
                      height: "",
                      download: true,
                    });
                  }}
                >
                  <CustomTooltip
                    icon={<DownloadIcon />}
                    message={t("Download Receipt")}
                  />
                </Link>
              </>
            )}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItem(item);
                setShowViewItem(true);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
          </div>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "failed";
              if (
                value === "submitted" ||
                value === "success" ||
                value === "completed"
              ) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "created_at":
              return formatDate(value);
            case "amount_transfered":
              return <p className="text-right">{CentsToDollars(value)}</p>;
            case "type":
              return <p className="text-uppercase">{value}</p>;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
